/**
 * Repository base. Wraps the Firestore SDK so feature code never touches it.
 *
 * Each concrete repo (tenantsRepo, platformBrandsRepo, ...) builds on this
 * to expose typed, schema-validated read/write/listen APIs.
 *
 * This file is one of the two files in apps/dashboard permitted to import
 * `firebase/firestore` (the other is `lib/firebase.ts`). The ESLint config
 * has an allow-list rule for `apps/*\/src/lib/repositories/**`.
 */

import {
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
  type Query,
  type QueryConstraint,
  type Unsubscribe,
  type OrderByDirection,
  type WhereFilterOp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy as fsOrderBy,
  query as fsQuery,
  setDoc,
  updateDoc,
  where as fsWhere,
  type WithFieldValue,
  type SetOptions,
} from 'firebase/firestore';
import type { z } from 'zod';

import { db } from '../firebase.js';
import { logger } from '../logger.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Firestore-agnostic bounded-query descriptor. Feature code passes plain
 * tuples (and allow-listed `Timestamp` values) so it never imports the query
 * builders — the repo layer is the only place that translates these into
 * real Firestore constraints. This is what lets `/reports` and `/customers`
 * subscribe to a bounded slice instead of scanning the whole collection
 * (audit 2026-07-05 §2 — the two free-tier breach paths).
 */
export interface BoundedQuery {
  /** `[fieldPath, op, value]` filters, e.g. `['payment.receivedAt', '>=', fromTs]`. */
  readonly where?: readonly (readonly [string, WhereFilterOp, unknown])[];
  /** `[fieldPath, direction?]` ordering, e.g. `['lastVisitAt', 'desc']`. */
  readonly orderBy?: readonly (readonly [string, OrderByDirection?])[];
  /** Hard cap on documents read/held by the listener. */
  readonly limitTo?: number;
}

export interface Repo<T> {
  get(id: string): Promise<T | null>;
  list(): Promise<T[]>;
  snapshot(id: string, cb: (value: T | null) => void): Unsubscribe;
  /**
   * Subscribe to the whole collection. Skips docs that fail schema validation.
   *
   * `onError` is optional but STRONGLY recommended — without it, a Firestore
   * permission / network error leaves the listener silently stalled and any
   * "loading…" UI hangs forever, violating [[feedback-no-dead-ends]]. New
   * callers should wire it; existing callers stay backward-compatible.
   */
  listSnapshot(cb: (values: T[]) => void, onError?: (err: Error) => void): Unsubscribe;
  /**
   * Like `listSnapshot`, but subscribes to a BOUNDED slice (where/orderBy/limit)
   * instead of the whole collection. Use this for any collection that grows
   * unbounded with tenant age (ticket history, customer index) so a page open
   * costs O(bounded) reads, not O(all-time). Bad docs are skipped, same as
   * `listSnapshot`.
   */
  listSnapshotBounded(
    q: BoundedQuery,
    cb: (values: T[]) => void,
    onError?: (err: Error) => void,
  ): Unsubscribe;
  /**
   * Overwrite a doc. Validates the shape (throws on invalid) but PERSISTS the
   * caller's ORIGINAL object — never the Zod-parsed clone. Zod's parse turns a
   * `Timestamp` into a plain object with function-valued `toMillis`/`toDate`
   * that Firestore rejects as an unencodable field value.
   *
   * HAZARD (ADR-0027): because the original object is persisted unmodified,
   * `set()` no longer strips stray fields (the ADR-0026 self-protection is
   * gone). Never echo a raw repo READ object in — reads now carry the doc
   * `id`, so a stray post-parse `id` would be written. Pass an explicit
   * field patch.
   */
  set(id: string, value: T, options?: SetOptions): Promise<void>;
  /**
   * Firestore-style partial update. Accepts a plain object (typed loose
   * here because Firestore SDK natively supports dotted-path keys like
   * `'profile.contact.email'` which the strict `PartialWithFieldValue<T>`
   * type does NOT model. Callers should pass either a partial of T or
   * a record with dotted-path keys.
   *
   * HAZARD (ADR-0026): reads now carry the doc `id`, and `update()` writes
   * the patch UNPARSED. Never echo a raw repo READ object here — pass an
   * explicit field patch, or a stray `id` would be persisted into the doc.
   */
  update(id: string, patch: Record<string, unknown>): Promise<void>;
  /**
   * Hard-delete a doc. Use only where the id is genuinely disposable and not
   * referenced elsewhere as a soft key (customer tags soft-delete via an
   * `archived` flag instead — see CustomerTagSchema). Loyalty tiers are safe
   * to hard-delete: `evalTier` treats a customer's now-stale `tierId` as null
   * and re-derives on their next award (eval-tier.ts edge cases).
   */
  delete(id: string): Promise<void>;
}

export interface RepoOptions<S extends z.ZodTypeAny> {
  /** Collection path; can be templated with the args passed to the factory. */
  readonly path: string;
  /** Zod schema; the repo will .parse() on every read and validate on every write. */
  readonly schema: S;
  /** Optional collection name for logs. */
  readonly name?: string;
}

/**
 * Generic is the **schema**, not the data type; the repo returns
 * `z.output<S>` (post-parse shape with defaults applied), eliminating
 * the input-vs-output type drift that caused ~60 false type errors
 * across the apps before the Sprint 47 audit-remediation fix.
 */
export function createRepo<S extends z.ZodTypeAny>(opts: RepoOptions<S>): Repo<z.output<S>> {
  type T = z.output<S>;
  const colRef = collection(db, opts.path) as CollectionReference<T & DocumentData>;

  const parseOrThrow = (raw: unknown): T => {
    const result = opts.schema.safeParse(raw);
    if (!result.success) {
      throw new SchemaValidationFailedError(opts.name ?? opts.path, result.error.flatten());
    }
    return result.data as T;
  };

  return {
    async get(id: string): Promise<T | null> {
      const snap = await getDoc(doc(colRef, id) as DocumentReference<T & DocumentData>);
      if (!snap.exists()) return null;
      // Attach the Firestore doc id AFTER the parse — never before. Zod
      // `z.object` STRIPS undeclared keys, so a pre-parse `{ id, ...data }`
      // silently dropped the id for any schema that didn't declare it (the
      // missing-id bug class). Re-attaching it as the last spread key
      // post-parse makes the id always survive, regardless of the schema —
      // the class is closed permanently. See ADR-0026.
      return { ...parseOrThrow(snap.data()), id };
    },
    async list(): Promise<T[]> {
      const snap = await getDocs(colRef as Query<T & DocumentData>);
      // id attached post-parse so it can never be stripped by the schema (ADR-0026).
      return snap.docs.map((d) => ({ ...parseOrThrow(d.data()), id: d.id }));
    },
    snapshot(id: string, cb: (value: T | null) => void): Unsubscribe {
      return onSnapshot(doc(colRef, id) as DocumentReference<T & DocumentData>, (snap) => {
        if (!snap.exists()) {
          cb(null);
          return;
        }
        try {
          // id attached post-parse so it can never be stripped by the schema (ADR-0026).
          cb({ ...parseOrThrow(snap.data()), id });
        } catch {
          cb(null);
        }
      });
    },
    listSnapshot(cb: (values: T[]) => void, onError?: (err: Error) => void): Unsubscribe {
      return onSnapshot(
        colRef as Query<T & DocumentData>,
        (snap) => {
          const out: T[] = [];
          let skipped = 0;
          for (const d of snap.docs) {
            try {
              // id attached post-parse so it can never be stripped by the schema (ADR-0026).
              out.push({ ...parseOrThrow(d.data()), id: d.id });
            } catch {
              // Skip individual bad docs rather than dropping the whole subscription.
              skipped++;
            }
          }
          if (skipped > 0) {
            // Skipping quietly is how a schema/fixture mismatch becomes an
            // unexplained empty screen: /reports showed "No tickets in last 30
            // days" while 13 matching docs sat in the database, every one of them
            // failing on a single missing field. A page with no rows and no
            // complaint is indistinguishable from a page with no data.
            logger.warn('bounded snapshot skipped unparseable docs', {
              repo: opts.name ?? opts.path,
              skipped,
              kept: out.length,
            });
          }
          cb(out);
        },
        (err) => {
          // Firestore-level listener error (permission, network, quota).
          // Callers that pass `onError` surface this; older callers that
          // don't pass it silently degrade — same behaviour as before.
          if (onError) onError(err);
        },
      );
    },
    listSnapshotBounded(
      q: BoundedQuery,
      cb: (values: T[]) => void,
      onError?: (err: Error) => void,
    ): Unsubscribe {
      const constraints: QueryConstraint[] = [];
      for (const [field, op, value] of q.where ?? []) constraints.push(fsWhere(field, op, value));
      for (const [field, dir] of q.orderBy ?? []) constraints.push(fsOrderBy(field, dir ?? 'asc'));
      if (q.limitTo !== undefined) constraints.push(fsLimit(q.limitTo));
      const bounded = fsQuery(colRef as Query<T & DocumentData>, ...constraints);
      return onSnapshot(
        bounded,
        (snap) => {
          const out: T[] = [];
          for (const d of snap.docs) {
            try {
              // id attached post-parse so it can never be stripped by the schema (ADR-0026).
              out.push({ ...parseOrThrow(d.data()), id: d.id });
            } catch {
              // Skip individual bad docs rather than dropping the whole subscription.
            }
          }
          cb(out);
        },
        (err) => {
          if (onError) onError(err);
        },
      );
    },
    async set(id: string, value: T, options?: SetOptions): Promise<void> {
      // Validate the shape (throws on invalid) but PERSIST the caller's ORIGINAL
      // object — never the Zod-parsed clone. Zod's object parser copies a
      // Firestore `Timestamp`'s prototype methods (`toMillis`/`toDate`) onto a
      // plain object, which `setDoc` then rejects: "Unsupported field value: a
      // function (found in field …toMillis)". That silently broke every
      // client-side `.set()` writing a Timestamp field (promotions, vouchers,
      // tags, campaigns) — none were e2e-covered, so it went unnoticed until the
      // promotions-builder e2e. Writing the original object keeps real Timestamp
      // instances intact. Schemas use no `.transform()`/coercion (verified), so
      // the only thing not persisted is Zod defaults — and those are re-applied
      // on every read, so nothing is lost.
      parseOrThrow(value);
      const ref = doc(colRef, id) as DocumentReference<T & DocumentData>;
      await (options
        ? setDoc(ref, value as WithFieldValue<T & DocumentData>, options)
        : setDoc(ref, value as WithFieldValue<T & DocumentData>));
    },
    async update(id: string, patch: Record<string, unknown>): Promise<void> {
      // Partial updates can't be schema-validated as a whole; rely on writer to know shape.
      await updateDoc(
        doc(colRef, id) as DocumentReference<T & DocumentData>,
        patch as DocumentData,
      );
    },
    async delete(id: string): Promise<void> {
      await deleteDoc(doc(colRef, id) as DocumentReference<T & DocumentData>);
    },
  };
}
